import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../partage/api/client';
import { useAuPouce } from '../partage/surface';
import { cles, relireApres } from '../partage/relectures';
import { delaiEnMots, enFrancais } from '../partage/datesEnMots';
import { motsDuDossier } from '../partage/vocabulaire';
import './Modele.css';

/**
 * D4 · Le modele « Acheter la maison »
 *
 * **C'etait l'unique template livre en V1** (SPEC 11). Depuis le ticket 41, un second s'y
 * choisit, « Creer une societe », de demonstration : ses rendez-vous sont inventes, et son
 * avertissement le dit. « Acheter la maison » reste celui qui s'ouvre, et celui qui sert a MONTRER, pas celui qui
 * sert a batir : un utilisateur qui arrive n'a rien a faire du permis, et un projet a huit
 * branches est exactement ce qu'il ne saurait pas saisir seul un lundi soir.
 *
 * L'ecran est construit sur la frontiere de la SPEC 3 : **ce qu'il apporte** — mis en
 * bleu-vert, parce que c'est ce que tu recuperes — et **ce qu'il ne pourra JAMAIS te
 * donner**, pose en neutre juste dessous : ton rang, tes raisons, tes lieux. Plus
 * l'avertissement sur les delais : ceux qui sont legaux sont exacts au jour pres, les autres
 * sont faits pour etre corriges.
 */
/** Un titre d'etape au milieu d'une phrase perd sa majuscule. */
function minuscule(titre: string): string {
  return titre.charAt(0).toLowerCase() + titre.slice(1);
}

export function Modele({ onFermer }: { onFermer: () => void }) {
  const naviguer = useNavigate();
  const client = useQueryClient();
  const templates = useQuery({ queryKey: cles.templates, queryFn: api.templates });
  const m = motsDuDossier(useAuPouce());

  const charger = useMutation({
    mutationFn: (cle: string) => api.chargerTemplate(cle),
    onSuccess: (resultat) => {
      void relireApres(client, 'chargerUnModele');
      naviguer(`/feuille/${resultat.dossierId}`);
    },
  });

  /*
   * Le premier modèle est « Acheter la maison » (le serveur range la démonstration après) :
   * le tuto compte le trouver ouvert, puisqu'il fait cliquer « Charger le modèle » sans en choisir
   * un (`aideAvance.ts`, « Partir d'un modèle et l'adapter »). Les autres se choisissent au-dessus
   * du titre.
   */
  const [choisi, setChoisi] = useState<string | null>(null);
  const tous = templates.data?.templates ?? [];
  const modele = tous.find((t) => t.cle === choisi) ?? tous[0];
  if (!modele) return null;

  return (
    <>
      <div className="modele-voile" onClick={onFermer} aria-hidden="true" />

      <div className="modele" role="dialog" aria-label={`le modele ${modele.titre}`}>
        <header className="modele-tete">
          <div>
            {tous.length > 1 ? (
              <div className="modele-choix" role="group" aria-label="choisir un modèle">
                {tous.map((t) => (
                  <button
                    key={t.cle}
                    type="button"
                    className="modele-choix-un"
                    aria-pressed={t.cle === modele.cle}
                    onClick={() => setChoisi(t.cle)}
                  >
                    {t.titre}
                    {t.demo && <span className="modele-choix-demo"> · démonstration</span>}
                  </button>
                ))}
              </div>
            ) : (
              <p className="modele-amorce">Le seul modèle de la version 1</p>
            )}
            <h2>{modele.titre}</h2>
          </div>
          <button type="button" className="modele-fermer" onClick={onFermer} aria-label="fermer">
            <svg width="17" height="17" viewBox="0 0 17 17" fill="none" aria-hidden="true">
              <path d="M3.5 3.5l10 10M13.5 3.5l-10 10" stroke="currentColor" strokeWidth="1.4" />
            </svg>
          </button>
        </header>

        <div className="modele-corps">
          {/*
            Ce que le modele pose, branche par branche. « 44 etapes » ne dit rien ; « le
            compromis · 60 jours de purge du droit de preemption » dit exactement ce qu'on
            gagne a le charger — c'est ce que personne ne sait de tete.
          */}
          <div className="modele-contenu">
            <p className="modele-contenu-titre">{m.poseDansTon}</p>

            {modele.branches.map((branche) => (
              <div className="modele-pose" key={branche.titre}>
                <span>{branche.titre}</span>
                {branche.delai ? (
                  <em className="est-du-monde">
                    {delaiEnMots({ delaiJours: branche.delai.jours, delaiMois: branche.delai.mois })}&nbsp;:{' '}
                    {minuscule(branche.delai.titre)}
                  </em>
                ) : (
                  <em>
                    {branche.combien > 0
                      ? `${branche.combien} étape${branche.combien > 1 ? 's' : ''}`
                      : 'À creuser'}
                  </em>
                )}
              </div>
            ))}

            <div className="modele-pose modele-pose--total">
              <span>{modele.combien} étapes en tout</span>
              {modele.verifieLe && (
                <em className="est-du-monde">
                  Délais vérifiés aux sources le {enFrancais(modele.verifieLe)}
                </em>
              )}
            </div>
          </div>

          <div className="modele-frontiere">
            <section className="modele-apporte">
              <h3>Ce qu’il t’apporte</h3>
              {(modele.presentation?.cequilapporte ?? []).map((ligne) => (
                <LigneAMot key={ligne} texte={ligne} />
              ))}
            </section>

            <section className="modele-jamais">
              <h3>Ce qu’il ne pourra jamais te donner</h3>
              {(modele.presentation?.cequilnedonnejamais ?? []).map((ligne) => (
                <LigneAMot key={ligne} texte={ligne} />
              ))}
            </section>

            {modele.presentation?.avertissement && (
              <section className="modele-avertissement">
                {/* Le pointille ambre : ce qui n'a jamais ete verifie CHEZ TOI. Le delai vient
                    du monde, et il est fait pour etre corrige (principe 5). */}
                <svg width="17" height="17" viewBox="0 0 19 19" fill="none" aria-hidden="true">
                  <circle
                    cx="9.5"
                    cy="9.5"
                    r="8.7"
                    stroke="var(--monde)"
                    strokeWidth="1.4"
                    strokeDasharray="2.6 3.2"
                  />
                </svg>
                <p>{modele.presentation.avertissement}</p>
              </section>
            )}
          </div>
        </div>

        <footer className="modele-pied">
          <p>
            Un modèle te fait gagner de la frappe, pas de la réflexion. Il ne remplace ni ta
            hiérarchie, ni tes délais réels&nbsp;— et {m.ecritALaMain} marche aussi bien.
          </p>
          <div className="modele-issues">
            <button type="button" className="modele-vide" onClick={onFermer}>
              {m.partirDUnVide}
            </button>
            <button
              className="bouton"
              onClick={() => charger.mutate(modele.cle)}
              disabled={charger.isPending}
            >
              {charger.isPending ? 'Un instant' : 'Charger le modèle'}
            </button>
          </div>
        </footer>
      </div>
    </>
  );
}

/**
 * Une ligne de la frontiere : un mot-cle, puis ce qu'il veut dire.
 *
 * Le template ecrit ses lignes sous la forme « le mot — l'explication ». Quand le tiret
 * manque, la ligne tient toute seule et occupe la largeur : on n'invente pas un mot-cle en
 * coupant la phrase au hasard.
 */
function LigneAMot({ texte }: { texte: string }) {
  const coupe = texte.split(/\s[—–]\s/);
  const mot = coupe.length > 1 ? coupe[0] : null;
  const reste = coupe.length > 1 ? coupe.slice(1).join(' — ') : texte;

  return (
    <p className="modele-ligne">
      {mot && <span className="modele-mot">{mot}</span>}
      <span className="modele-dit">{reste}</span>
    </p>
  );
}
